<template>
  <div>
    <div class="row justify-content-md-center m-0">
      <b-col
        cols="10"
        xl="7"
      >
        <b-card
          class="mt-5"
          no-body
        >
          <b-card-header>
            <h3 class="card-title">
              Campaign Summary
            </h3>
          </b-card-header>
          <b-card-body>
            <p>
              Please check your campaign configuration before proceeding to
              invoice and campaign approvals
            </p>
            <div class="row">
              <div class="col-6">
                <ReviewCardItem
                  label="Title"
                  :text="campaign.name"
                  data-test="campaign-summary-name"
                />
                <ReviewCardItem
                  label="Campaign Periods"
                  data-test="campaign-periods"
                >
                  <div
                    v-for="(item, index) in order.periods"
                    :key="`campaign-period-${index}`"
                  >
                    <span class="tag tag-primary text-white">{{ formatDateForPlatform(item.startDate) }} - {{ formatDateForPlatform(item.endDate) }}</span>
                  </div>
                </ReviewCardItem>
                <ReviewCardItem
                  label="Targeting"
                  text=""
                  data-test="campaign-summary-geo-targeting"
                  custom-class="mb-0"
                >
                  <div
                    class="tags"
                  >
                    <template v-if="hasRegionTargeting">
                      <h6 class="mt-3">
                        Region
                      </h6>
                      <span
                        v-for="(id) in order.geoTargeting.regions"
                        :key="`region-${id}`"
                        class="tag tag-primary"
                      >
                        {{ getRegionName(id) }}
                      </span>
                    </template>
                    <template v-if="hasIncludeTargeting">
                      <h6 class="mt-3">
                        Include
                      </h6>
                      <span
                        v-for="(item, index) in getCountryInclusions"
                        :key="`include-${index}`"
                        class="tag tag-success include"
                      >
                        {{ getCountyName(item) }}
                      </span>
                    </template>
                    <template v-if="hasExcludeTargeting">
                      <h6 class="mt-3">
                        Exclude
                      </h6>
                      <span
                        v-for="(item, index) in getCountryExclusions"
                        :key="`exclude-${index}`"
                        class="tag tag-danger exclude"
                      >
                        {{ getCountyName(item) }}
                      </span>
                    </template>
                  </div>
                </ReviewCardItem>
              </div>
              <div class="col-6">
                <ReviewCardItem
                  label="Content"
                  :text="totalArticles"
                  data-test="campaign-summary-total-articles"
                />
                <ReviewCardItem
                  label="Total Budget"
                  :text="formattedMoneyAmount(this.order.budget)"
                  data-test="campaign-summary-budget"
                />
                <ReviewCardItem
                  label="Brand safety keywords"
                  :text="totalKeywords"
                  data-test="campaign-summary-total-keywords"
                />
                <ReviewCardItem
                  label="Creative sizes"
                  :text="allCreativeSizes"
                  data-test="campaign-summary-creative-size"
                />
                <feature
                  name="CUSTOM_PUBLISHERS"
                >
                  <ReviewCardItem
                    label="Publisher List"
                    :text="getPublisherList"
                    data-test="campaign-summary-publisher-list"
                    custom-class="mb-0"
                  />
                </feature>
              </div>
            </div>
            <br>
            <h6>
              Require Assistance?
            </h6>
            <p>
              If you need help with any aspect of your campaign or account, you can contact
              us via email at <strong>
                <a
                  href="mailto:support@smartology.net"
                  target="_blank"
                >support@smartology.net</a>
              </strong>
            </p>
          </b-card-body>
        </b-card>
      </b-col>
    </div>
  </div>
</template>
<script>
import ReviewCardItem from '@/modules/campaigns/components/ReviewCardItem';
import { mapState, mapActions, mapGetters } from 'vuex';

import currenciesMixin from '@/core/mixins/currencies';

import dateHelper from '@/core/mixins/dateHelper';
import helper from '@/core/helper';
import * as contentTypes from '@/modules/content/store/types';
import * as campaignTypes from '@/modules/campaigns/store/types';

export default {
  name: 'CampaignSummary',
  mixins: [
    currenciesMixin,
    dateHelper,
  ],
  components: {
    ReviewCardItem,
  },
  data() {
    return {
      allCreativeSizes: null,
    };
  },
  computed: {
    ...mapState('campaigns', [
      'campaign',
      'order',
      'currencies',
      'countries',
      'regions',
      'blacklist',
      'publisherList',
      'publisherIds',
    ]),
    ...mapState('content', [
      'newContentItems',
      'creative',
      'creatives',
    ]),
    ...mapGetters('campaigns', [
      campaignTypes.GET_CAMPAIGN_MAX_END_DATE,
      campaignTypes.GET_IS_MANAGED_CAMPAIGN,
      campaignTypes.GET_IS_SELF_SERVICE_CAMPAIGN_ACCESSED_BY_ADMIN,
    ]),
    warningModalTitle() {
      return this.GET_IS_MANAGED_CAMPAIGN ? 'Your campaign cannot go live' : 'Your campaign cannot be sent for approval';
    },
    warningModalContentFirstPart() {
      return this.GET_IS_MANAGED_CAMPAIGN ? 'Your campaign has one or more period with an end date in the past.' : 'Your campaign has one or more period with an end date in the past.';
    },
    warningModalContentSecondPart() {
      return this.GET_IS_MANAGED_CAMPAIGN ? 'Please change the affected dates in the Basic Info tab before setting your campaign live.' : 'Before sending your campaign for approval, please change the affected dates in the Basic Info tab.';
    },
    totalKeywords() {
      if (Object.keys(this.blacklist).length === 0) return 'No keywords';
      return `${this.blacklist.blacklistKeywords.length}`;
    },
    totalArticles() {
      if (this.newContentItems.length === 0) return 'No articles';
      const displayText = this.newContentItems.length > 1 ? 'articles' : 'article';
      return `${this.newContentItems.length} ${displayText}`;
    },
    hasIncludeTargeting() {
      return (this.order.geoTargeting.countryInclusionsExclusions.inclusions.length !== 0);
    },
    hasExcludeTargeting() {
      return (this.order.geoTargeting.countryInclusionsExclusions.exclusions.length !== 0);
    },
    hasRegionTargeting() {
      return this.order.geoTargeting.regions.length > 0;
    },
    getCountryInclusions() {
      return this.order.geoTargeting.countryInclusionsExclusions.inclusions;
    },
    getCountryExclusions() {
      return this.order.geoTargeting.countryInclusionsExclusions.exclusions;
    },
    getPublisherList() {
      let publisherList = helper.orderBy(this.publisherList, 'name');
      publisherList = publisherList
        .filter((publisher) => this.publisherIds.includes(publisher.id));
      return publisherList.map((publisher) => publisher.name).toString().replace(/,/g, ', ');
    },
  },
  methods: {
    ...mapActions('content', [
      contentTypes.FETCH_ARTICLES,
    ]),
    getCountyName(name) {
      return this.countries[name];
    },
    getRegionName(id) {
      if (!this.regions.find((i) => i.id === id)) return 'Name not found';
      return this.regions.find((i) => i.id === id).name;
    },
    getCreativeSizesCounts(creativeSizes) {
      const counts = {};
      creativeSizes.forEach((x) => { counts[x] = (counts[x] || 0) + 1; });
      return counts;
    },
    setupCreativeSizesText(creatives) {
      let text = '';
      const allCreativeSizes = creatives.map((creative) => creative.sizeName).sort();
      const creativeSizesCount = this.getCreativeSizesCounts(allCreativeSizes);
      for (const [key, value] of Object.entries(creativeSizesCount)) {
        text += `(${key}) x ${value}\n`;
      }
      this.allCreativeSizes = text;
    },
    formattedMoneyAmount(budget) {
      return this.getFormattedMoneyAmountByOrder(budget, this.order);
    },
  },
  created() {
    if (this.GET_CAMPAIGN_MAX_END_DATE != null && this.GET_CAMPAIGN_MAX_END_DATE && this.isDateInThePast(this.GET_CAMPAIGN_MAX_END_DATE)
        && !this.GET_IS_SELF_SERVICE_CAMPAIGN_ACCESSED_BY_ADMIN) {
      this.$emit('setIsValidForNavigation', 'next', false);
      const h = this.$createElement;
      this.$bvModal.msgBoxOk(h('p', [
        this.warningModalContentFirstPart, h('br'), h('br'), this.warningModalContentSecondPart,
      ]), {
        title: this.warningModalTitle,
        size: 'md',
        buttonSize: 'xl',
        okTitle: 'OK',
        okVariant: 'primary',
        footerClass: 'p-2',
        hideHeaderClose: false,
        centered: true,
      });
    }
  },
  mounted() {
    if (this.creatives.length > 0) {
      const activeCreatives = this.creatives.filter((creative) => !creative.paused);
      this.setupCreativeSizesText(activeCreatives);
    }

    this.FETCH_ARTICLES();
  },
};
</script>
